import {useIntl} from 'react-intl'
import {MessageItemWrapper} from './MessageItemWrapper'
import DateRangePicker from 'react-bootstrap-daterangepicker'
import moment from 'moment'
import {useMemo, useState} from 'react'
import clsx from 'clsx'
import {
  FORMAT_CHATBOT_DATE_TIME,
  MESSAGE_TYPE,
  MessageDateTimeRangePayload,
  MessageItemCommonProps,
} from '../../definitions'
import {Button} from '../../../../../_gori/partials/widgets'
import {KTSVG} from '../../../../../_gori/helpers'
import {useChatbotContext} from '../../context'

type MessageItemDateRangePickerProps = MessageItemCommonProps<MessageDateTimeRangePayload> & {
  alwaysShowLabel?: boolean
}

export const MessageItemDateRangePicker = ({
  message,
  className = '',
  alwaysShowLabel = true,
}: MessageItemDateRangePickerProps) => {
  const intl = useIntl()
  const [shouldRender, setShouldRender] = useState(true)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const {handleCreateMessage, disabledUserAction} = useChatbotContext()

  const [date, setDate] = useState<{
    start: moment.Moment | null
    end: moment.Moment | null
  }>({
    start: message?.content?.value?.start_date
      ? moment(message?.content?.value?.start_date)
      : moment().subtract(29, 'days'),
    end: message?.content?.value?.end_date ? moment(message?.content?.value?.end_date) : moment(),
  })

  const handleReset = () => {
    setShouldRender(false)
    setTimeout(() => setShouldRender(true), 0)
  }

  const formatDate = ({start, end}: {start: moment.Moment | null; end: moment.Moment | null}) => {
    return {
      start: start?.format(process.env.REACT_APP_DATE_FORMAT) || '',
      end: end?.format(process.env.REACT_APP_DATE_FORMAT) || '',
    }
  }

  const labelDate = useMemo(() => {
    const {start, end} = formatDate(date)

    if (start && end) return `${start} - ${end}`
    if (alwaysShowLabel) return ''
    return undefined
  }, [alwaysShowLabel, date])

  const handleChangeDate = (start: moment.Moment, end: moment.Moment) => {
    setDate({start: moment(start), end: moment(end)})
  }

  const handleDeleteField = () => {
    if (disabled) return

    setDate({start: null, end: null})
    handleReset()
  }

  const disabled = useMemo(
    () => disabledUserAction || message?.content?.disabled || isSubmitted,
    [disabledUserAction, message?.content?.disabled, isSubmitted]
  )

  const renderContent = () => (
    <div
      className={clsx(
        'btn btn-sm bg-white rounded-2 d-flex justify-content-center align-items-center',
        className,
        {
          'rounded-2': labelDate,
          'text-hover-primary-200': !disabled,
          'cursor-not-allowed': disabled,
        }
      )}
    >
      {labelDate ? (
        <>
          <div>{labelDate}</div>
          {(date.start || date.end) && (
            <Button
              event={handleDeleteField}
              className={clsx(
                'text-hover-danger border-0 ms-2 bg-transparent rounded-circle bg-hover-white py-1',
                {
                  'cursor-not-allowed': disabled,
                }
              )}
            >
              <KTSVG
                path='/media/gori/orders/delete.svg'
                className='opacity-50 m-0'
                svgClassName='mh-10px'
              />
            </Button>
          )}
        </>
      ) : (
        <span className='text-gray-600'>MM/DD/YYYY - MM/DD/YYYY</span>
      )}
      <KTSVG path='/media/gori/chatbot/calendar.svg' className={clsx('svg-icon svg-icon-1 ms-3')} />
    </div>
  )

  const onSubmit = async () => {
    if (disabled) return

    try {
      await handleCreateMessage({
        type: MESSAGE_TYPE.DATE_RANGE_PICKER,
        currentMessageId: message.id,
        message: {
          name: 'date_range',
          value: {
            start_date: moment(date.start).format(FORMAT_CHATBOT_DATE_TIME),
            end_date: moment(date.end).format(FORMAT_CHATBOT_DATE_TIME),
          },
        },
      })
      setIsSubmitted(true)
    } catch {}
  }

  return (
    <MessageItemWrapper
      className='bg-primary-200 d-flex flex-fill flex-column gap-3 w-100'
      message={message}
      wrapperClassName='align-self-end'
    >
      <>
        {shouldRender && !disabled ? (
          <DateRangePicker
            initialSettings={{
              locale: {
                customRangeLabel: intl.formatMessage({id: 'CUSTOM_RANGE'}),
                cancelLabel: intl.formatMessage({id: 'CANCEL'}),
                applyLabel: intl.formatMessage({id: 'APPLY'}),
                fromLabel: intl.formatMessage({id: 'FROM'}),
                toLabel: intl.formatMessage({id: 'TO'}),
                daysOfWeek: [
                  intl.formatMessage({id: 'SU'}),
                  intl.formatMessage({id: 'MO'}),
                  intl.formatMessage({id: 'TU'}),
                  intl.formatMessage({id: 'WE'}),
                  intl.formatMessage({id: 'TH'}),
                  intl.formatMessage({id: 'FR'}),
                  intl.formatMessage({id: 'SA'}),
                ],
                monthNames: [
                  intl.formatMessage({id: 'JANUARY'}),
                  intl.formatMessage({id: 'FEBRUARY'}),
                  intl.formatMessage({id: 'MARCH'}),
                  intl.formatMessage({id: 'APRIL'}),
                  intl.formatMessage({id: 'MAY'}),
                  intl.formatMessage({id: 'JUNE'}),
                  intl.formatMessage({id: 'JULY'}),
                  intl.formatMessage({id: 'AUGUST'}),
                  intl.formatMessage({id: 'SEPTEMBER'}),
                  intl.formatMessage({id: 'OCTOBER'}),
                  intl.formatMessage({id: 'NOVEMBER'}),
                  intl.formatMessage({id: 'DECEMBER'}),
                ],
              },
              startDate: date.start ? date.start?.toDate() : undefined,
              endDate: date.end ? date.end?.toDate() : undefined,
              opens: 'top',
              ranges: {
                [`${intl.formatMessage({id: 'TODAY'})}`]: [moment(), moment()],
                [`${intl.formatMessage({id: 'YESTERDAY'})}`]: [
                  moment().subtract(1, 'days'),
                  moment().subtract(1, 'days'),
                ],
                [`${intl.formatMessage({id: 'LAST_7_DAYS'})}`]: [
                  moment().subtract(6, 'days'),
                  moment(),
                ],
                [`${intl.formatMessage({id: 'LAST_30_DAYS'})}`]: [
                  moment().subtract(29, 'days'),
                  moment(),
                ],
                [`${intl.formatMessage({id: 'LAST_MONTH'})}`]: [
                  moment().subtract(1, 'month').startOf('month'),
                  moment().subtract(1, 'month').endOf('month'),
                ],
              },
            }}
            onCallback={handleChangeDate}
          >
            {renderContent()}
          </DateRangePicker>
        ) : (
          renderContent()
        )}
      </>
      <div
        className={clsx(
          'h-40px rounded-2 border-0 d-flex justify-content-center align-items-center fw-bold',
          {
            'bg-gray-300 cursor-not-allowed text-gray-500': disabled,
            'bg-white bg-hover-light-primary cursor-pointer text-primary-200': !disabled,
          }
        )}
        onClick={onSubmit}
      >
        {intl.formatMessage({id: 'SUBMIT'})}
      </div>
    </MessageItemWrapper>
  )
}
